/**
 * Central language config. All shell + service packages import from here so
 * adding a language later is one edit, not a repo-wide sweep.
 */

/** ISO 639-1 code we use in URLs, localStorage, and Accept-Language headers.
 *  Kazakh is Cyrillic-first (`kk`); Latin (`kk-Latn`) is a future add. */
export type Lang = 'en' | 'ru' | 'kk';

export const DEFAULT_LANG: Lang = 'en';

/** Fallback chain read by react-i18next: KK missing → try RU → try EN.
 *  RU is closer to administrative Kazakh vocabulary than English is, so it's
 *  a better human fallback than sending KK users to English mid-page. */
export const FALLBACK: Record<Lang, Lang[]> = {
  en: ['en'],
  ru: ['ru', 'en'],
  kk: ['kk', 'ru', 'en'],
};

/** Display metadata shown in the language switcher. `native` is what the
 *  user sees in each row of the dropdown regardless of the current locale
 *  — a Russian user shopping for KK should see "Қазақша", not "Kazakh". */
export interface LangDescriptor {
  code: Lang;
  native: string; // shown in the dropdown row
  english: string; // used for accessibility labels + telemetry
  /** Locale string passed to Intl.NumberFormat / Intl.DateTimeFormat.
   *  `kk-KZ` is understood by all modern JS engines; the Cyrillic script is
   *  the default variant so we don't need to spell it as `kk-Cyrl-KZ`. */
  intlLocale: string;
  /** Cloudscape locale key. Cloudscape ships built-ins for `en`, `de`, `fr`,
   *  `ja`, `ko`, `pt-BR`, `zh-CN` only; `ru` and `kk` do not exist upstream,
   *  so we pass hand-written messages via <I18nProvider messages> — the
   *  string here is only the locale name Cloudscape will echo back. */
  cloudscapeLocale: string;
}

export const LANGUAGES: readonly LangDescriptor[] = [
  { code: 'en', native: 'English', english: 'English', intlLocale: 'en-US', cloudscapeLocale: 'en' },
  { code: 'ru', native: 'Русский', english: 'Russian', intlLocale: 'ru-RU', cloudscapeLocale: 'ru' },
  { code: 'kk', native: 'Қазақша', english: 'Kazakh', intlLocale: 'kk-KZ', cloudscapeLocale: 'kk' },
];

export function isLang(v: unknown): v is Lang {
  return typeof v === 'string' && LANGUAGES.some((l) => l.code === v);
}

export function descriptor(lang: Lang): LangDescriptor {
  return LANGUAGES.find((l) => l.code === lang) ?? LANGUAGES[0]!;
}

/** localStorage key holding the user's persisted choice. Scoped to `vex.`
 *  so we don't collide with app-specific keys. */
export const LS_KEY = 'vex.console.lang';
